import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/api';

export interface Upload {
  id: string;
  url: string;
  thumbnailUrl: string;
  filename: string;
  uploadedAt: string;
}

// API response shapes
interface UploadsResponse {
  success: boolean;
  uploads: Upload[];
}

interface UploadResponse {
  success: boolean;
  upload: Upload;
}

export const useImageLibrary = () => {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUploads = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await apiClient.get<UploadsResponse>('/api/design/uploads');
      if (!response.success) {
        setError(response.error || 'Failed to load your images');
      } else if (response.data) {
        setUploads(response.data.uploads || []);
      }
    } catch (err) {
      console.error('Failed to fetch uploads:', err);
      setError('Failed to load your images. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addUpload = useCallback(async (url: string, thumbnailUrl?: string, filename?: string) => {
    try {
      setError(null);
      const response = await apiClient.post<UploadResponse>('/api/design/uploads', {
        url,
        thumbnailUrl: thumbnailUrl || url,
        filename: filename || 'upload',
      });
      if (!response.success) {
        setError(response.error || 'Failed to save image to library');
      } else if (response.data?.upload) {
        setUploads((prev) => [response.data!.upload, ...prev]);
      }
    } catch (err) {
      console.error('Failed to add upload:', err);
      setError('Failed to save image to library');
    }
  }, []);

  const removeUpload = useCallback(async (id: string) => {
    // Store previous state for rollback
    const previousUploads = uploads;

    // Optimistic update
    setUploads((prev) => prev.filter((u) => u.id !== id));
    setError(null);

    try {
      const response = await apiClient.delete<{ success: boolean }>(`/api/design/uploads/${id}`);
      if (!response.success) {
        // Rollback on failure
        setUploads(previousUploads);
        setError(response.error || 'Failed to delete image');
      }
    } catch (err) {
      console.error('Failed to remove upload:', err);
      // Rollback on error
      setUploads(previousUploads);
      setError('Failed to delete image. Please try again.');
    }
  }, [uploads]);

  useEffect(() => {
    fetchUploads();
  }, [fetchUploads]);

  return {
    uploads,
    isLoading,
    error,
    addUpload,
    removeUpload,
    refetch: fetchUploads,
    clearError: () => setError(null),
  };
};
